import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Package, Heart, User as UserIcon } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, getAuthHeaders } from '../lib/utils';
import { ProductCard } from '../components/ProductCard';

export const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const { user, logout } = useAuth();
  const defaultTab = searchParams.get('tab') || 'orders';

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
    fetchWishlist();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`, {
        headers: getAuthHeaders(),
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API_URL}/wishlist`, {
        headers: getAuthHeaders(),
      });
      setWishlist(response.data);

      // Fetch product details
      if (response.data.product_ids && response.data.product_ids.length > 0) {
        const productRequests = response.data.product_ids.map((id) =>
          axios.get(`${API_URL}/products/${id}`)
        );
        const products = await Promise.all(productRequests);
        setWishlistProducts(products.map((p) => p.data));
      }
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen py-12 px-6 lg:px-12 bg-purple-25" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-light mb-4" data-testid="welcome-message">
            Welcome, {user.name}
          </h1>
          <p className="text-stone-600">{user.email}</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={defaultTab} className="w-full" data-testid="dashboard-tabs">
          <TabsList className="w-full grid grid-cols-3 mb-8">
            <TabsTrigger value="orders" data-testid="orders-tab">
              <Package className="mr-2 h-4 w-4" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="wishlist" data-testid="wishlist-tab">
              <Heart className="mr-2 h-4 w-4" />
              Wishlist
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="profile-tab">
              <UserIcon className="mr-2 h-4 w-4" />
              Profile
            </TabsTrigger>
          </TabsList>

          {/* Orders */}
          <TabsContent value="orders" data-testid="orders-content">
            <h2 className="text-2xl font-heading mb-6">Your Orders</h2>
            {orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map((order, index) => (
                  <Card key={order.id} className="p-6" data-testid={`order-${index}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-heading text-lg mb-1">Order #{order.id.substring(0, 8)}</p>
                        <p className="text-sm text-stone-600">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-heading" data-testid="order-total">
                          ${order.total_amount.toFixed(2)}
                        </p>
                        <span className="inline-block px-3 py-1 rounded-full text-xs bg-green-100 text-green-800 mt-2">
                          {order.status}
                        </span>
                      </div>
                    </div>
                    <div className="border-t border-stone-200 pt-4">
                      {order.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex justify-between mb-2" data-testid={`order-item-${itemIndex}`}>
                          <span className="text-stone-600">
                            {item.product_name} ({item.size}) × {item.quantity}
                          </span>
                          <span>${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center" data-testid="no-orders">
                <Package className="mx-auto h-12 w-12 text-stone-400 mb-4" />
                <p className="text-stone-600 mb-4">No orders yet</p>
                <button
                  onClick={() => navigate('/shop')}
                  className="text-primary hover:underline"
                >
                  Start Shopping
                </button>
              </Card>
            )}
          </TabsContent>

          {/* Wishlist */}
          <TabsContent value="wishlist" data-testid="wishlist-content">
            <h2 className="text-2xl font-heading mb-6">Your Wishlist</h2>
            {wishlistProducts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {wishlistProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center" data-testid="no-wishlist">
                <Heart className="mx-auto h-12 w-12 text-stone-400 mb-4" />
                <p className="text-stone-600 mb-4">Your wishlist is empty</p>
                <button
                  onClick={() => navigate('/shop')}
                  className="text-primary hover:underline"
                >
                  Discover Products
                </button>
              </Card>
            )}
          </TabsContent>

          {/* Profile */}
          <TabsContent value="profile" data-testid="profile-content">
            <h2 className="text-2xl font-heading mb-6">Profile Settings</h2>
            <Card className="p-6 mb-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-stone-600 mb-1">Name</p>
                  <p className="text-lg" data-testid="profile-name">{user.name}</p>
                </div>
                <div>
                  <p className="text-sm text-stone-600 mb-1">Email</p>
                  <p className="text-lg" data-testid="profile-email">{user.email}</p>
                </div>
                <div>
                  <p className="text-sm text-stone-600 mb-1">Member Since</p>
                  <p className="text-lg">
                    {new Date(user.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </Card>

            <button
              onClick={handleLogout}
              className="text-red-600 hover:underline"
              data-testid="logout-button"
            >
              Logout
            </button>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};